// hooks/transcripts/queries.js - Updated to use excerpt keys from excerpts module
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import QualAPI from 'utils/QualAPI';
import { buildParagraphs } from 'utils/ParagraphUtils';
import { mapExcerptsToParagraphs } from '../utils/excerptMappingUtils';
import { excerptKeys, reportMissingExcerptDetails } from '../excerpts/queries';
// Function-scope use only (module cycle: this file <-> transcriptCacheUtils
// — safe because nothing touches the binding at module-eval time).
import { seedExcerptDetails } from '../utils/transcriptCacheUtils';
import { surveyKeys } from '../surveys/queries';
import { normalizeId } from '../utils/idNormalization';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

// Define query keys for transcripts (without excerpt-related keys)
export const transcriptKeys = {
  all: ['transcripts'],
  lists: () => [...transcriptKeys.all, 'list'],
  list: (projectId) => [...transcriptKeys.lists(), projectId],
  details: () => [...transcriptKeys.all, 'detail'],
  // Normalized like the factory's other keys: callers pass route-param
  // strings and numeric ids interchangeably, and an unnormalized key mints
  // a duplicate cache entry that the exact-key bulk writers never update
  // and mutation cancelQueries never cancel.
  detail: (id) => [...transcriptKeys.details(), normalizeId(id)],
  names: () => [...transcriptKeys.all, 'name'],
  name: (id) => [...transcriptKeys.names(), normalizeId(id)],
  counts: () => [...transcriptKeys.all, 'count'],
  count: (projectId) => [...transcriptKeys.counts(), normalizeId(projectId)],
  dashboards: () => [...transcriptKeys.all, 'dashboard'],
  dashboard: (projectId) => [...transcriptKeys.dashboards(), normalizeId(projectId)],
  coverages: () => [...transcriptKeys.all, 'coverage'],
  coverage: (projectId, ids) => [...transcriptKeys.coverages(), normalizeId(projectId), (ids || []).join(',')],
  coverageItem: (projectId, id) => [...transcriptKeys.coverages(), normalizeId(projectId), 'item', normalizeId(id)]
};

/**
 * Hook to fetch the Transcripts dashboard LIST for a project: per-transcript
 * assignees (including the derived 'bot' assignee), coding recency, and the
 * cheap counts (snippet_count, coder_count). Coverage bars are fetched
 * separately and lazily via useTranscriptCoverage.
 *
 * The endpoint is paginated; like useAllProjectTranscripts, this loads
 * batches sequentially and updates the cache progressively, so each request
 * stays small no matter how large the project is.
 *
 * @param {string|number} projectId - The project ID
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Query result with a flat array of dashboard transcript rows
 */
export function useTranscriptDashboard(projectId, options = {}) {
  const { enabled = true } = options;
  const normalizedProjectId = normalizeId(projectId);
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: transcriptKeys.dashboard(normalizedProjectId),
    queryFn: async ({ signal }) => {
      const limit = 500;
      const dashboardKey = transcriptKeys.dashboard(normalizedProjectId);
      // Progressive partial writes are for the INITIAL load only. On a
      // background refetch they would truncate the full cached array (and
      // clobber optimistic assignee updates) while batches stream in; there
      // the final return swaps the data in atomically instead.
      const hadCachedData = queryClient.getQueryData(dashboardKey) !== undefined;
      // cancelQueries (fired by mutation onMutate) detaches this promise but
      // can't stop the loop — bail out explicitly so a cancelled loop never
      // writes stale rows over a newer optimistic update.
      const assertNotCancelled = () => {
        if (signal?.aborted) throw new Error('dashboard batch load cancelled');
      };

      const firstBatch = await QualAPI.getTranscriptDashboard(projectId, { offset: 0, limit });
      const totalCount = firstBatch.data.total_count;
      let allRows = firstBatch.data.transcripts;

      // Seed the per-transcript coverage cache with the bundled first page so
      // its bars render without a second round-trip (useTranscriptCoverage
      // then only fetches the ids that aren't already cached).
      (firstBatch.data.initial_coverage || []).forEach(row => {
        queryClient.setQueryData(transcriptKeys.coverageItem(normalizedProjectId, row.id), row);
      });

      if (totalCount > limit) {
        assertNotCancelled();
        if (!hadCachedData) {
          queryClient.setQueryData(dashboardKey, allRows);
        }

        let offset = limit;
        while (offset < totalCount) {
          const batch = await QualAPI.getTranscriptDashboard(projectId, { offset, limit });
          allRows = [...allRows, ...batch.data.transcripts];

          assertNotCancelled();
          if (!hadCachedData) {
            queryClient.setQueryData(dashboardKey, allRows);
          }

          offset += limit;
        }
      }

      return allRows;
    },
    enabled: enabled && !!projectId,
    staleTime: 30 * 1000, // recency goes stale quickly while teammates code
    // Always refetch when the dashboard is (re)mounted — e.g. navigating back
    // after editing/coding a transcript — so the "Last updated" order reflects
    // the new recency without a hard reload. The cached rows stay visible while
    // the background refetch runs, then swap in atomically (see queryFn).
    refetchOnMount: 'always',
    retry: (failureCount, error) => {
      if (error?.name === 'HTTP404Error' || error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
}

/**
 * Hook to fetch the per-coder coverage bars for a specific set of transcript
 * ids — the dashboard's currently-visible page. The expensive island merge is
 * therefore only computed for what's on screen. Cached per id-set, so paging
 * back is a cache hit.
 *
 * @param {string|number} projectId - The project ID
 * @param {Array<number>} transcriptIds - The visible transcript ids
 * @param {Object} options - Query options
 * @returns {Object} Query result whose data is a map { [id]: { coders } }
 */
export function useTranscriptCoverage(projectId, transcriptIds, options = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const normalizedProjectId = normalizeId(projectId);
  const ids = [...(transcriptIds || [])].map(normalizeId).sort((a, b) => a - b);

  // Coverage is cached per transcript id. Ids already cached — seeded with the
  // first page, or fetched on an earlier page — are read straight from the
  // cache; only the misses are fetched. This makes the bundled first page an
  // instant, requestless hit without depending on an exact id-set match.
  const seeded = {};
  const missing = [];
  ids.forEach(id => {
    const cached = queryClient.getQueryData(transcriptKeys.coverageItem(normalizedProjectId, id));
    if (cached !== undefined) seeded[id] = cached; else missing.push(id);
  });

  const query = useQuery({
    queryKey: transcriptKeys.coverage(normalizedProjectId, missing),
    queryFn: () => QualAPI.getTranscriptCoverage(projectId, missing).then(response => {
      const byId = {};
      (response.data.coverage || []).forEach(row => {
        byId[row.id] = row;
        queryClient.setQueryData(transcriptKeys.coverageItem(normalizedProjectId, row.id), row);
      });
      return byId;
    }),
    enabled: enabled && !!projectId && missing.length > 0,
    placeholderData: keepPreviousData, // hold the prior page's bars until the new ones arrive
    staleTime: 30 * 1000,
    retry: (failureCount, error) => {
      if (error?.name === 'HTTP404Error' || error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });

  return { ...query, data: { ...seeded, ...(query.data || {}) } };
}

export function useTranscriptName(transcriptId) {
  const normalizedTranscriptId = normalizeId(transcriptId);
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: transcriptKeys.name(normalizedTranscriptId),
    queryFn: async () => {
      // Fallback: if not in cache, try to find it in transcript list caches
      const listQueries = queryClient.getQueriesData({ queryKey: transcriptKeys.lists() });
      for (const [, transcriptList] of listQueries) {
        if (Array.isArray(transcriptList)) {
          const transcript = transcriptList.find(t =>
            normalizeId(t.id) === normalizedTranscriptId
          );

          if (transcript) return transcript;
        }
      }

      throw new Error(`Transcript ${transcriptId} not found in any cached transcript list`);
    },
    enabled: !!normalizedTranscriptId,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000, // 30 minutes instead of Infinity
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch the count of transcripts for a project
 * This is more efficient than fetching transcripts when you only need the count
 * @param {string|number} projectId - The project ID
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Query result with transcript count
 */
export function useTranscriptCount(projectId, options = {}) {
  const { enabled = true } = options;
  const normalizedProjectId = normalizeId(projectId);

  return useQuery({
    queryKey: transcriptKeys.count(normalizedProjectId),
    queryFn: () => QualAPI.getProjectTranscripts(projectId, { offset: 0, limit: 1 })
      .then(response => response.data.total_count),
    enabled: enabled && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error?.name === 'HTTP404Error' || error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
}

/**
 * Hook to fetch ALL transcripts for a project (loads all batches automatically)
 * This is useful for components that need the complete list like filters and dropdowns.
 *
 * Note: If you only need the transcript count, use useTranscriptCount instead.
 *
 * Loads batches sequentially to avoid hammering the server with parallel requests.
 * Returns first 500 immediately, then loads remaining batches in background.
 *
 * @param {string|number} projectId - The project ID
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Query result with all transcripts (flattened array)
 */
export function useAllProjectTranscripts(projectId, options = {}) {
  const { enabled = true } = options;
  const normalizedProjectId = normalizeId(projectId);
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: transcriptKeys.list(normalizedProjectId),
    queryFn: async () => {
      const limit = 500;
      let allTranscripts = [];
      let offset = 0;

      // Fetch first batch to get total count
      const firstBatch = await QualAPI.getProjectTranscripts(projectId, { offset: 0, limit });
      const totalCount = firstBatch.data.total_count;

      allTranscripts = firstBatch.data.transcripts;

      // Cache first batch transcripts
      allTranscripts.forEach(transcript => {
        queryClient.setQueryData(
          transcriptKeys.name(normalizeId(transcript.id)),
          transcript
        );
      });

      // If there are more transcripts, load them in the background
      // but return the first batch immediately
      if (totalCount > limit) {
        // Set the query data immediately with first batch
        queryClient.setQueryData(
          transcriptKeys.list(normalizedProjectId),
          allTranscripts
        );

        // Continue loading remaining batches in background
        offset = limit;
        while (offset < totalCount) {
          const batch = await QualAPI.getProjectTranscripts(projectId, { offset, limit });

          // Add transcripts from this batch
          allTranscripts = [...allTranscripts, ...batch.data.transcripts];

          // Cache each transcript
          batch.data.transcripts.forEach(transcript => {
            queryClient.setQueryData(
              transcriptKeys.name(normalizeId(transcript.id)),
              transcript
            );
          });

          // Update the query data progressively as we load more
          queryClient.setQueryData(
            transcriptKeys.list(normalizedProjectId),
            allTranscripts
          );

          offset += limit;
        }
      }

      return allTranscripts;
    },
    enabled: enabled && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error?.name === 'HTTP404Error' || error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
}


/**
 * Hook to fetch transcript data
 *
 * The cache entry carries `excerpt_ids` only (ids-only model): excerpt
 * objects live in the detail caches — resolve them via useExcerpt(id) or
 * useTranscriptExcerpts(transcriptId).
 *
 * @param {string|number} transcriptId - The ID of the transcript to fetch
 * @returns {Object} The query result containing transcript data, loading state, and error
 */
export function useTranscript(transcriptId) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: transcriptKeys.detail(transcriptId),
    queryFn: ({ signal }) => QualAPI.getTranscript(transcriptId)
      .then(response => {
        const data = response.data;

        // Ids-only boundary transform. The SERVER payload deliberately
        // keeps embedding full excerpts — a transcript body edit remaps
        // every excerpt's start/end server-side (Transcript#update_body's
        // diff mapper), and this refetch's seeding is what re-delivers the
        // remapped offsets in bulk. The CACHE entry stores only
        // excerpt_ids: excerpt objects live solely in the detail caches,
        // so codings changes touch one cache and no sync layer exists.
        //
        // Seeding never runs from a cancelled fetch: the coding mutations
        // cancel the owning transcript's refetch before their optimistic
        // writes, and while react-query discards a cancelled query's
        // result, this side effect would still run when the late response
        // lands — unguarded, it wrote pre-mutation codings over the
        // optimistic update (see excerpts/queries.cancellationRace.test).
        //
        // Ordering invariant (see transcriptCacheUtils): details are
        // seeded here, in the queryFn, and the ids-bearing entry commits
        // afterwards with the query result — details always land first.
        if (data?.excerpts && Array.isArray(data.excerpts)) {
          // healMembership: false — every membership write here would
          // target the stale entry this queryFn's return value is about
          // to wholesale-replace.
          seedExcerptDetails(queryClient, data.excerpts, signal, { healMembership: false });
          const { excerpts, ...rest } = data;
          return { ...rest, excerpt_ids: excerpts.map(excerpt => normalizeId(excerpt.id)) };
        }

        return data;
      }),
    enabled: !!transcriptId,
    retry: (failureCount, error) => {
      // Don't retry on 404 or 403 errors
      if (error?.response?.status === 404 ||
          error?.response?.status === 403 ||
          error?.status === 404 ||
          error?.status === 403) {
        return false;
      }

      // For other errors, retry up to 3 times
      return failureCount < 3;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get processed paragraphs from transcript data
 */
function useTranscriptParagraphs(transcriptBody) {
  const paragraphs = useMemo(() => {
    if (!transcriptBody) return [];
    return buildParagraphs(transcriptBody);
  }, [transcriptBody]);

  return { data: paragraphs };
}

/**
 * Utility function to find paragraphs that overlap with an excerpt
 * @param {Array} paragraphs - Array of paragraph objects
 * @param {number} excerptStart - Start position of excerpt
 * @param {number} excerptEnd - End position of excerpt
 * @returns {Array} Array of overlapping paragraphs
 */
export function findOverlappingParagraphs(paragraphs, excerptStart, excerptEnd) {
  if (!paragraphs || !Array.isArray(paragraphs)) return [];
  
  return paragraphs.filter(paragraph => 
    !(excerptEnd <= paragraph.start || excerptStart >= paragraph.end)
  );
}

/**
 * Helper function to get paragraphs for a codeable item from the cache
 * @param {Object} queryClient - React Query client
 * @param {number} codeableId - The codeable item ID (transcript or survey answer)
 * @param {string} codeableType - The type of codeable item ('Transcript' or 'SurveyAnswer')
 * @returns {Array} Array of paragraph objects
 */
function getParagraphsForCodeable(queryClient, codeableId, codeableType) {
  let body = null;
  
  if (codeableType === 'Transcript') {
    // For transcripts, use the transcript data from the cache
    body = queryClient.getQueryData(transcriptKeys.detail(codeableId))?.body;
  } else if (codeableType === 'SurveyAnswer') {
    // For survey answers, use the survey answer data from the cache
    body = queryClient.getQueryData(surveyKeys.answer(codeableId))?.body;
  }
  
  return body ? buildParagraphs(body) : [];
}

/**
 * Utility function to add an excerpt to paragraph mappings
 * @param {Object} queryClient - React Query client
 * @param {number} codeableId - The codeable item ID (transcript or survey answer)
 * @param {string} codeableType - The type of codeable item ('Transcript' or 'SurveyAnswer')
 * @param {number} excerptId - Excerpt ID (can be temporary)
 * @param {number} start - Excerpt start position
 * @param {number} end - Excerpt end position
 * @returns {Array} Array of affected paragraphs
 */
export function addExcerptToParagraphs(queryClient, codeableId, codeableType = 'Transcript', excerptId, start, end) {
  // Get paragraphs using the helper function
  const paragraphs = getParagraphsForCodeable(queryClient, codeableId, codeableType);
  const affectedParagraphs = findOverlappingParagraphs(paragraphs, start, end);
  
  // Update paragraph excerpt mappings
  affectedParagraphs.forEach(paragraph => {
    const currentExcerptIds = queryClient.getQueryData(
      excerptKeys.paragraphExcerptIds(codeableId, codeableType, paragraph.start, paragraph.end)
    ) || [];
    
    // Add the excerpt ID if it's not already there
    if (!currentExcerptIds.includes(excerptId)) {
      queryClient.setQueryData(
        excerptKeys.paragraphExcerptIds(codeableId, codeableType, paragraph.start, paragraph.end),
        [...currentExcerptIds, excerptId]
      );
    }
  });
  
  return affectedParagraphs;
}

/**
 * Utility function to remove an excerpt from paragraph mappings
 * @param {Object} queryClient - React Query client
 * @param {number} codeableId - The codeable item ID (transcript or survey answer)
 * @param {string} codeableType - The type of codeable item ('Transcript' or 'SurveyAnswer')
 * @param {number} excerptId - Excerpt ID to remove
 * @param {Array} affectedParagraphs - Paragraphs to update (optional, will calculate if not provided)
 * @param {number} start - Excerpt start position (required if affectedParagraphs not provided)
 * @param {number} end - Excerpt end position (required if affectedParagraphs not provided)
 */
export function removeExcerptFromParagraphs(queryClient, codeableId, codeableType = 'Transcript', excerptId, affectedParagraphs = null, start = null, end = null) {
  let paragraphsToUpdate = affectedParagraphs;
  
  // If paragraphs not provided, calculate them
  if (!paragraphsToUpdate && start !== null && end !== null) {
    const paragraphs = getParagraphsForCodeable(queryClient, codeableId, codeableType);
    paragraphsToUpdate = findOverlappingParagraphs(paragraphs, start, end);
  }
  
  if (!paragraphsToUpdate) return;
  
  // Remove excerpt ID from paragraph mappings
  paragraphsToUpdate.forEach(paragraph => {
    const currentExcerptIds = queryClient.getQueryData(
      excerptKeys.paragraphExcerptIds(codeableId, codeableType, paragraph.start, paragraph.end)
    ) || [];
    
    const updatedIds = currentExcerptIds.filter(id => id !== excerptId);
    queryClient.setQueryData(
      excerptKeys.paragraphExcerptIds(codeableId, codeableType, paragraph.start, paragraph.end),
      updatedIds
    );
  });
}

/**
 * Utility function to replace a temporary excerpt ID with a real one in paragraph mappings
 * @param {Object} queryClient - React Query client
 * @param {number} codeableId - The codeable item ID (transcript or survey answer)
 * @param {string} codeableType - The type of codeable item ('Transcript' or 'SurveyAnswer')
 * @param {string|number} tempExcerptId - Temporary excerpt ID
 * @param {number} realExcerptId - Real excerpt ID from server
 * @param {Array} affectedParagraphs - Paragraphs to update
 */
export function replaceExcerptIdInParagraphs(queryClient, codeableId, codeableType = 'Transcript', tempExcerptId, realExcerptId, affectedParagraphs) {
  if (!affectedParagraphs) return;
  
  affectedParagraphs.forEach(paragraph => {
    const currentExcerptIds = queryClient.getQueryData(
      excerptKeys.paragraphExcerptIds(codeableId, codeableType, paragraph.start, paragraph.end)
    ) || [];
    
    // Replace temporary ID with real ID
    const updatedIds = currentExcerptIds.map(id => 
      id === tempExcerptId ? realExcerptId : id
    );
    
    queryClient.setQueryData(
      excerptKeys.paragraphExcerptIds(codeableId, codeableType, paragraph.start, paragraph.end),
      updatedIds
    );
  });
}

/**
 * Initialize paragraph excerpt mappings from transcript data
 * This is the proper place for this since transcript data originates here
 * @param {string|number} transcriptId - The transcript ID
 * @param {string} codeableType - The type of codeable item (always 'Transcript' for this function)
 */
export function useInitializeParagraphExcerpts(transcriptId, codeableType = 'Transcript') {
  const { data: transcriptData } = useTranscript(transcriptId);
  const { data: paragraphs } = useTranscriptParagraphs(transcriptData?.body);
  const queryClient = useQueryClient();
  
  // Positions live in the detail caches now (ids-only model). The mapping
  // must re-run when membership changes or when a refetch re-seeds offsets
  // (a body edit remaps every excerpt's start/end server-side) — both of
  // which mint a new transcriptData identity. Coding-only changes never
  // touch the transcript entry, so edits no longer re-run this scan at all
  // (previously gated by a positions signature; now free by construction).
  useEffect(() => {
    const excerptIds = transcriptData?.excerpt_ids;
    if (!excerptIds || !paragraphs) return;

    // A real (numeric) member with no detail entry is an invariant
    // violation (see reportMissingExcerptDetails): reported, then mapped
    // over the entries that exist.
    reportMissingExcerptDetails(queryClient, transcriptData);
    mapExcerptsToParagraphs(
      paragraphs,
      excerptIds.map((id) => queryClient.getQueryData(excerptKeys.detail(id))).filter(Boolean),
      transcriptId,
      codeableType,
      queryClient
    );
  }, [transcriptData, paragraphs, transcriptId, codeableType, queryClient]);
}

/**
 * Hook to get a specific paragraph text from transcript data
 * @param {string|number} transcriptId - The transcript ID
 * @param {number} paragraphId - The paragraph index to retrieve
 * @returns {Object} Object containing paragraph text and loading state
 */
export function useTranscriptParagraph(transcriptId, paragraphId) {
  const { data: transcriptData, isLoading, error } = useTranscript(transcriptId);
  const { data: paragraphs } = useTranscriptParagraphs(transcriptData?.body);
  
  const paragraph = useMemo(() => {
    if (!paragraphs || paragraphId === null || paragraphId === undefined) return null;
    
    // Find the paragraph by its index
    return paragraphs.find(p => p.paragraphIndex === paragraphId) || null;
  }, [paragraphs, paragraphId]);

  return {
    text: paragraph?.text || '',
    isLoading,
    error,
    paragraph
  };
}

/**
 * Hook to return transcriptID if on a transcript page
 * @returns {Object|null} Object containing transcriptID if on a transcript page, otherwise null
 */
export const useCurrentTranscript = () => {
  const location = useLocation();
  if (location?.pathname?.includes("/transcripts/")) {
    const pathParts = location.pathname.split("/");
    const transcriptID = pathParts[2];
    return { transcriptID };
  }

  return null
}